import { fetchClientViewVehicles } from "../../api/clientViewVehicleApi";
import { formatVehicleTitle } from "../../utils/automapListing/listingFormatters";
import { trackRecommendationEvent as trackCentralRecommendationEvent } from "../../utils/buyerAnalytics";
import { BUDGET_OPTION_DEFS } from "./buyerConciergeQuizConstants";
import {
  rankInventoryRecommendations,
  rankMockRecommendations,
} from "./buyerRecommendationMatching";

const MIN_RECOMMENDATIONS = 3;
const TARGET_RECOMMENDATIONS = 4;

function buildInventoryQuery(intent, profileId) {
  const option = BUDGET_OPTION_DEFS.find((entry) => entry.value === intent.budgetRange);
  const customMax = intent.customMaxBudget ? Number(intent.customMaxBudget) : null;
  const params = {
    page: 1,
    pageSize: 40,
    sort: "best_value_score",
  };
  if (profileId) params.profile = profileId;

  if (option?.priceMin != null) params.price_min = option.priceMin;
  const maxPrice = customMax || option?.priceMax;
  if (maxPrice != null) params.price_max = maxPrice;

  if (intent.transmission === "automatico") params.transmission = "automatic";
  if (intent.transmission === "mecanico") params.transmission = "manual";

  return params;
}

function mergeRecommendations(primary, fallback, limit = TARGET_RECOMMENDATIONS) {
  const seen = new Set();
  const merged = [];
  [...primary, ...fallback].forEach((item) => {
    if (seen.has(item.id)) return;
    seen.add(item.id);
    merged.push(item);
  });
  return merged.slice(0, limit);
}

export async function fetchBuyerRecommendations({ slug, intent, profileId, locale = null }) {
  if (!intent) {
    return {
      recommendations: [],
      source: "none",
      partialMatch: false,
      error: null,
    };
  }

  try {
    const response = await fetchClientViewVehicles(slug, buildInventoryQuery(intent, profileId));
    const body = response?.data || response;
    const listings = body?.items || body?.listings || [];
    const ranked = rankInventoryRecommendations(listings, intent, slug, locale);
    const strongMatches = ranked.filter((item) => item.matchScore >= 70);

    if (strongMatches.length >= MIN_RECOMMENDATIONS) {
      return {
        recommendations: strongMatches.slice(0, TARGET_RECOMMENDATIONS),
        source: "inventory",
        partialMatch: false,
        error: null,
      };
    }

    if (ranked.length >= MIN_RECOMMENDATIONS) {
      return {
        recommendations: ranked.slice(0, TARGET_RECOMMENDATIONS),
        source: "inventory",
        partialMatch: true,
        error: null,
      };
    }

    const mockRanked = rankMockRecommendations(intent, slug);
    const recommendations =
      ranked.length > 0
        ? mergeRecommendations(ranked, mockRanked)
        : mockRanked.slice(0, TARGET_RECOMMENDATIONS);

    return {
      recommendations: recommendations.slice(0, TARGET_RECOMMENDATIONS),
      source: ranked.length ? "inventory+mock" : "mock",
      partialMatch: ranked.length > 0 && ranked.length < MIN_RECOMMENDATIONS,
      error: null,
    };
  } catch (error) {
    const mockRanked = rankMockRecommendations(intent, slug);
    return {
      recommendations: mockRanked.slice(0, TARGET_RECOMMENDATIONS),
      source: "mock",
      partialMatch: false,
      error: error?.message || "inventory_unavailable",
    };
  }
}

export function buildRecommendationWhatsappHref(recommendation, buyerName) {
  const vehicleTitle = formatVehicleTitle(recommendation || {}, { fallback: "vehículo" });
  const greeting = buyerName ? `Hola, soy ${buyerName}.` : "Hola.";
  const message = encodeURIComponent(
    `${greeting} Me interesa el ${vehicleTitle} que vi recomendado en AutoMapa. ¿Sigue disponible?`
  );
  return `https://wa.me/573001234567?text=${message}`;
}

const RECOMMENDATION_EVENT_BRIDGE = {
  recommendation_results_viewed: "recommendation_results_viewed",
  recommendation_clicked: "recommendation_card_clicked",
  recommendation_whatsapp_clicked: "recommendation_contact_clicked",
  recommendation_saved: "recommendation_saved",
  recommendation_compared: "recommendation_compare_added",
};

export function trackRecommendationEvent(track, eventType, metadata = {}) {
  if (typeof track === "function") {
    track(eventType, { component: "buyer_quiz_results", ...metadata });
  }
  const eventName = RECOMMENDATION_EVENT_BRIDGE[eventType] || eventType;
  trackCentralRecommendationEvent(eventName, metadata);
}
